/**
 * Turns an inbound link into something safe to report.
 *
 * Deep links into this app carry credentials in the open: the magic link carries a
 * sign-in `token` and the account's `email`, the OAuth callback carries a `code`, and
 * the community and content-library share routes put a bearer token in the path itself.
 * A link is followed from a mail client we do not control, so anything we report about
 * it leaves our hands entirely — which is why nothing here reports a URL verbatim.
 *
 * The query is an allowlist rather than a deny list. A deny list is written against the
 * links that exist today and silently starts leaking the moment a new one adds a
 * parameter nobody thought to ban; the allowlist fails the other way, dropping a new
 * campaign parameter until someone adds it, which shows up as a missing column rather
 * than as a token in a warehouse.
 *
 * The path is the exception, since it is the whole point of the event — it is reported
 * as written, with the two share routes whose last segment is a credential masked.
 */

import { DEEP_LINK_SCHEME } from '@/constants/links';
import { takeCommunicationAttributionProperties } from '@/util/communicationAttribution';
import { extractAppPath } from '@/utils/linkHelpers';

/**
 * Query parameters worth reporting on a followed link. Everything else is dropped,
 * including anything that identifies or authenticates the person following it.
 *
 * `source` and `action` are the pair the backend already stamps on `referencesReady`
 * notifications; the `utm_*` set matches `firstTouchAttribution`, so a campaign reads
 * the same whether it is measured as a touch or as an open. `via` is the platform tag the
 * API puts on the link an expert link's preview card opens.
 */
export const REPORTED_LINK_PARAMS = [
  'source',
  'action',
  'via',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'referred_by',
] as const;

export type ReportedLinkParam = (typeof REPORTED_LINK_PARAMS)[number];

export type DeepLinkParams = Partial<Record<ReportedLinkParam, string>>;

/**
 * How the link reached us. `scheme` is a `primum://` link, `universal` an https link on
 * an app domain that opened the native app, `web` an ordinary browser load.
 */
export type DeepLinkType = 'scheme' | 'universal' | 'web';

/** Whether the link started the app or arrived at one already running. */
export type DeepLinkLaunchType = 'cold' | 'warm';

export interface DeepLinkDescription {
  /** Route path, with credential-bearing segments masked. */
  path: string;
  /** True when the URL used the `primum://` scheme rather than an app domain. */
  isSchemeLink: boolean;
  params: DeepLinkParams;
}

/**
 * Route prefixes whose next path segment is a share token — a bearer credential that
 * grants access to the shared item, so it is masked exactly like a query token would be.
 */
const CREDENTIAL_PATH_PREFIXES = ['/community/shared/', '/content-library/shared/'];

const REDACTED = '[redacted]';

/**
 * Masks the credential segment of a share path and leaves every other path alone.
 *
 * Ids that are not credentials — a conversation sid, an expert id — are kept. They are
 * what makes a landing path answerable ("which inquiry did the email open?"), and they
 * already flow through screen tracking.
 */
export function redactPath(pathname: string): string {
  /*
   * Tolerates an absent path rather than throwing on one. This runs on every screen
   * change, and the root layout elsewhere treats the router's path as possibly not there
   * yet — a throw here would take out screen tracking entirely rather than one event.
   */
  if (!pathname) {
    return pathname;
  }

  /*
   * Matched case-insensitively: the router does not route a differently-cased share path,
   * but the link still reaches analytics as written, token included.
   */
  const lowerCasePathname = pathname.toLowerCase();

  const prefix = CREDENTIAL_PATH_PREFIXES.find(
    (candidate) =>
      lowerCasePathname.startsWith(candidate) && lowerCasePathname.length > candidate.length
  );

  if (!prefix) {
    return pathname;
  }

  const rest = pathname.slice(prefix.length);

  const nextSlash = rest.indexOf('/');

  return nextSlash === -1 ? `${prefix}${REDACTED}` : `${prefix}${REDACTED}${rest.slice(nextSlash)}`;
}

/**
 * Splits a `path?query#hash` string into its path and its query.
 *
 * Hand-rolled rather than routed through `URL`, because the input here is already a
 * relative path and `URL` needs an origin to parse one — inventing a base just to throw
 * it away invites the base leaking back into what we report.
 */
function splitPathAndQuery(pathWithQuery: string): { pathname: string; query: string } {
  const withoutHash = pathWithQuery.split('#')[0] ?? '';

  const queryStart = withoutHash.indexOf('?');

  if (queryStart === -1) {
    return { pathname: withoutHash, query: '' };
  }

  return {
    pathname: withoutHash.slice(0, queryStart),
    query: withoutHash.slice(queryStart + 1),
  };
}

/**
 * Reads the reportable parameters out of a query string.
 *
 * An empty value is treated as absent, so `?utm_source=` does not report a campaign
 * whose name is the empty string.
 */
function readReportedParams(query: string): DeepLinkParams {
  if (!query) {
    return {};
  }

  const searchParams = new URLSearchParams(query);

  const params: DeepLinkParams = {};

  for (const name of REPORTED_LINK_PARAMS) {
    const value = searchParams.get(name);

    if (value) {
      params[name] = value;
    }
  }

  return params;
}

/**
 * Describes a route path that is already known to be ours — the web entry point, where
 * the current location is by definition this app and there is no domain to check.
 *
 * Kept separate from `describeDeepLink` so that web tracking does not depend on the
 * app-domain allowlist: on a preview or localhost origin that check fails, and a
 * landing-page measurement that silently stops working outside production is worse
 * than no measurement at all, because it looks like it works.
 */
export function describeLinkPath(pathWithQuery: string): Omit<DeepLinkDescription, 'isSchemeLink'> {
  const { pathname, query } = splitPathAndQuery(pathWithQuery);

  return {
    path: redactPath(pathname || '/'),
    params: readReportedParams(query),
  };
}

/**
 * Describes an inbound URL, or returns null when it is not one of ours.
 *
 * Foreign URLs are rejected rather than reported as an open with a blank path: on
 * native the OS hands us only our own links, so a URL that fails this check is a sign
 * the scheme or domain config drifted, and a null keeps that from being counted as
 * traffic.
 */
export function describeDeepLink(url: string): DeepLinkDescription | null {
  const appPath = extractAppPath(url);

  if (!appPath) {
    return null;
  }

  return {
    ...describeLinkPath(appPath),
    isSchemeLink: url.startsWith(DEEP_LINK_SCHEME),
  };
}

/**
 * The attempt id and channel that the backend tags every notification link with, read
 * back off a launch URL.
 *
 * These answer which channel a person actually followed, rather than which channels were
 * sent — the question a low push number is otherwise unable to distinguish from a push
 * channel nobody can see. They ride on the events rather than being left for the warehouse
 * to parse off a page URL, on every platform: a push tapped on a phone deep-links straight
 * into the app, where nothing downstream ever sees a URL at all, and in a browser the
 * router rebuilds the address bar from its own state as soon as it resolves a route, which
 * drops any parameter no route declares before the first page event can fire.
 *
 * Absent on notifications sent before the tag shipped. They are omitted rather than sent
 * empty, because an empty channel reads as "arrived carrying none", which is a different
 * claim from "we do not know".
 */
export interface CommunicationAttribution {
  /**
   * Forwarded verbatim. It is base64url and the warehouse matches encoded to encoded, so
   * decoding it here would silently stop every row from joining.
   */
  communicationId?: string;
  communicationChannel?: string;
}

/**
 * Reads the attempt id and channel out of a notification's launch URL.
 *
 * Reads the query off the raw string rather than going through the app-link parsing
 * above, because a launch URL is the provider's to construct: it can carry a scheme or a
 * domain this app does not recognize, and attribution should survive that rather than
 * being dropped along with the link.
 */
export function readCommunicationAttribution(url: string | undefined): CommunicationAttribution {
  if (!url) {
    return {};
  }

  const queryStart = url.indexOf('?');

  if (queryStart === -1) {
    return {};
  }

  const searchParams = new URLSearchParams(url.slice(queryStart + 1).split('#')[0] ?? '');

  const communicationId = searchParams.get('cid');

  const communicationChannel = searchParams.get('ch');

  return {
    ...(communicationId ? { communicationId } : {}),
    ...(communicationChannel ? { communicationChannel } : {}),
  };
}

/** True when a description carries any campaign or channel attribution at all. */
export function hasReportedParams(params: DeepLinkParams): boolean {
  return Object.keys(params).length > 0;
}

/**
 * Screen-tracking parameters, with credential and identity values masked.
 *
 * Screen calls pass `useGlobalSearchParams()` through wholesale, so every parameter on
 * the current route rides along — including the magic link's sign-in token. This is a
 * deny list rather than an allowlist because screen properties are open-ended by design
 * and every route's params are meant to arrive; the credential names are the closed set,
 * not the useful ones.
 *
 * Values are masked rather than dropped so the fact that a screen was reached with a
 * token stays visible, which is the part worth knowing.
 *
 * `email` is deliberately absent, and should not be added back without asking the person
 * who took the decision. It is the only identifier that ties a marketing-email recipient
 * to their arrival when they never sign in, so masking it does not protect a session, it
 * removes the only join those visits have. The privacy cost of carrying an address here
 * was weighed and accepted; the credentials below were not, because each of them grants
 * access on its own.
 */
const SENSITIVE_PARAM_NAMES = new Set([
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'code',
  // The password-reset code, which arrives on an emailed link beside the address it resets.
  'verification',
  'password',
  'secret',
  'signature',
  // The SMART EHR launch handle, an opaque token the health system issues per launch.
  'launch',
]);

/**
 * Masks the credentials inside a value that carries a whole route rather than one field.
 *
 * Native route params include the raw route, query string and all, under a name of its
 * own. Masking by name cannot see into that: the name is the innocuous one while the
 * value holds the magic link's token and address in full, so name-only masking redacts
 * the fields and then ships the identical values one key over.
 *
 * The fragment is dropped rather than masked. It can carry a credential of its own — an
 * implicit-grant callback puts the access token there — and it is worth nothing to
 * analytics, so reporting it at all is risk without a reader. Keeping it and masking only
 * what is recognized would be worse than dropping it: the value would read as sanitised
 * while carrying whatever the next callback shape puts after the hash.
 *
 * Each surviving pair keeps its original spelling rather than being re-encoded, because
 * the same address has to arrive under one spelling however many other parameters rode
 * with it. Round-tripping through `URLSearchParams` decodes and re-encodes, which turns
 * one identifier into two the warehouse cannot join.
 */
function redactRouteValue(value: string): string {
  const beforeHash = value.split('#')[0] ?? '';

  const queryStart = beforeHash.indexOf('?');

  const rawPath = queryStart === -1 ? beforeHash : beforeHash.slice(0, queryStart);

  const query = queryStart === -1 ? '' : beforeHash.slice(queryStart + 1);

  // Share-path masking matches on a leading slash, which a route value arrives without.
  const isRooted = rawPath.startsWith('/');

  const maskedPath = redactPath(isRooted ? rawPath : `/${rawPath}`);

  const path = isRooted ? maskedPath : maskedPath.slice(1);

  const pairs = query ? query.split('&') : [];

  const isSensitivePair = (pair: string): boolean => {
    const separator = pair.indexOf('=');

    if (separator <= 0 || separator === pair.length - 1) {
      return false;
    }

    return SENSITIVE_PARAM_NAMES.has(decodeURIComponent(pair.slice(0, separator)).toLowerCase());
  };

  if (path === rawPath && beforeHash === value && !pairs.some(isSensitivePair)) {
    return value;
  }

  if (!query) {
    return path;
  }

  const maskedQuery = pairs
    .map((pair) =>
      isSensitivePair(pair) ? `${pair.slice(0, pair.indexOf('='))}=${REDACTED}` : pair
    )
    .join('&');

  return `${path}?${maskedQuery}`;
}

/**
 * Masks an address the web analytics library collects on its own, from the browser's
 * location rather than from the router: a page call's `url`, `search` and `referrer`, on
 * every event's page context. Masking screen params cannot reach these, so a credential in
 * the address bar, such as the magic link's token or the SMART launch handle, would ship in
 * them verbatim. Keeps the origin, masks path and query like a route value, drops the
 * fragment. A value that does not parse as a URL is a bare query string, Segment's `search`.
 */
export function redactPageAddress(value: string): string {
  if (!value) {
    return value;
  }

  if (value.startsWith('?')) {
    const masked = redactRouteValue(value);

    return masked.startsWith('?') ? masked : '';
  }

  try {
    const url = new URL(value);

    return `${url.origin}${redactRouteValue(url.pathname + url.search)}`;
  } catch {
    return redactRouteValue(value);
  }
}

/**
 * The name and properties a screen call may report, with credentials masked and the
 * notification tags this entry arrived with folded in.
 *
 * Exists so the masking can be asserted somewhere. The call site is a route file, which
 * this project's tooling cannot colocate a test with, so without a seam here the one
 * regression worth catching — passing the raw path and params straight through — breaks
 * no test.
 *
 * Deliberately not pure. The tags are readable only at entry, and they name the send that
 * caused it, so the first screen described after an entry takes them and later ones do not.
 */
export function describeScreen<Value>(
  pathname: string,
  params: Record<string, Value>
): { screenName: string; screenParams: Record<string, Value | string | string[]> } {
  return {
    screenName: redactPath(pathname),
    screenParams: {
      ...redactScreenParams(params),
      ...takeCommunicationAttributionProperties(),
    },
  };
}

/**
 * Names a screen by its route template, such as `/inbox/chat/[id]`, for tools that group
 * sessions by screen. The concrete path gives every conversation and profile its own
 * name, which splits a heatmap into one bucket per record. Route groups are dropped since
 * they never appear in a URL. Built from route segments, so it carries no param values
 * and needs no masking.
 */
export function describeRouteTemplate(segments: readonly string[]): string {
  const visibleSegments = segments.filter(
    (segment) => !(segment.startsWith('(') && segment.endsWith(')'))
  );

  return `/${visibleSegments.join('/')}`;
}

export function redactScreenParams<Value>(
  params: Record<string, Value>
): Record<string, Value | string | string[]> {
  const redacted: Record<string, Value | string | string[]> = {};

  for (const [name, value] of Object.entries(params)) {
    /*
     * Expo Router carries the URL fragment as a `#` param. It is dropped for the reason
     * route values drop theirs: the SMART callback's login token arrives there.
     */
    if (name === '#') {
      continue;
    }

    if (SENSITIVE_PARAM_NAMES.has(name.toLowerCase()) && value) {
      redacted[name] = REDACTED;

      continue;
    }

    if (typeof value === 'string') {
      redacted[name] = redactRouteValue(value);

      continue;
    }

    /*
     * A route param is typed as a string or an array of them, and an array reaches here
     * whenever a name repeats in the query — so masking only the string case would leave
     * a duplicated credential name untouched.
     */
    redacted[name] = Array.isArray(value)
      ? value.map((entry) => (typeof entry === 'string' ? redactRouteValue(entry) : entry))
      : value;
  }

  return redacted;
}
